import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { PageHeader } from "../../components/PageHeader";
import { IntegracionCard } from "./IntegracionCard";

const BASE = process.env.REACT_APP_PUBLIC_URL;

const Code = ({ children }) => (
  <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">{children}</pre>
);

const Envios = ({ tick }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/company/integracion/envios").then((r) => setRows(r.data));
  }, [tick]);
  const est = (r) => (r.entregado ? "Entregado" : r.descartado ? "Descartado" : `Reintento ${fmtFecha(r.proximo)}`);
  return (
    <section className="rounded-xl border bg-white p-5 space-y-3" data-testid="integracion-envios">
      <h2 className="font-heading text-lg font-semibold">Registro de envíos del webhook</h2>
      {rows.length === 0 ? <p className="text-sm text-slate-500">Aún no hay envíos.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2">Fecha</th><th>Evento</th><th>Estado</th><th>HTTP</th><th>Intentos</th><th>Entrega</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid="integracion-envio-row">
                  <td className="py-2">{fmtFecha(r.ultimo_intento || r.creado)}</td><td>{r.tipo}</td><td>{r.estado_cliente}</td>
                  <td>{r.http_status ?? r.ultimo_error ?? "—"}</td><td>{r.intentos}</td><td>{est(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const Docs = () => (
  <section className="rounded-xl border bg-white p-5 space-y-4 text-sm text-slate-700" data-testid="integracion-docs">
    <h2 className="font-heading text-lg font-semibold">Documentación</h2>
    <p>Todas las llamadas llevan el header <code>X-API-Key</code>. Solo ves clientes de tu empresa; un cliente de otra empresa responde 404.</p>
    <h3 className="font-semibold">1. Crear invitación</h3>
    <Code>{`POST ${BASE}/api/integracion/invitaciones
X-API-Key: <tu clave>
Content-Type: application/json

{"external_ref": "CLI-1234", "nombre": "María Pérez", "whatsapp": "+56912345678"}

→ {"inticheck_id": "…", "link": "${BASE}/k/…", "expira_at": "…", "estado": "pendiente"}`}</Code>
    <p>Si repites el mismo <code>external_ref</code> recibes el mismo <code>inticheck_id</code> (y el mismo link mientras siga vigente; uno nuevo si venció). Los links duran 7 días.</p>
    <p>Campos opcionales: <code>whatsapp_verificado: true</code> (tu app ya verificó el WhatsApp: no se vuelve a pedir y queda como verificado), <code>datos</code> para prellenar (correo, ocupacion, proposito, origen_fondos, pais_destino, domicilio) y <code>return_url</code>: https y del mismo dominio que el «Link de tu app» configurado en Marca. Al terminar, el cliente ve «Recibimos tus datos» y vuelve a <code>return_url</code> (o al Link de tu app) a los 5 segundos.</p>
    <h3 className="font-semibold">2. Consultar cliente</h3>
    <Code>{`GET ${BASE}/api/integracion/clientes/CLI-1234
X-API-Key: <tu clave>

→ {"inticheck_id": "…", "estado": "aprobado_provisional", "limite_mensual_clp": 200000, "actualizado_at": "…",
   "nivel_riesgo": "bajo", "fecha_aprobacion": "…", "fecha_vencimiento_ficha": "…",
   "nombre_completo": "…", "tipo_documento": "…", "numero_documento": "…", "nacionalidad": "…",
   "fecha_nacimiento": "1990-05-14", "fecha_vencimiento_documento": "2030-01-31",
   "ocupacion": "…", "origen_fondos": "sueldo",
   "telefono": "+569…", "telefono_verificado": true, "email": "…"}`}</Code>
    <p><code>origen_fondos</code>: sueldo, independiente, ahorros, ayuda_familiar, o el texto que escribió el cliente si eligió "otro". Las fechas van en formato AAAA-MM-DD.</p>
    <p>Estados: pendiente, en_revision, aprobado_provisional, aprobado, rechazado, bloqueado, requiere_actualizacion. Los datos personales solo vienen si el estado es aprobado o aprobado_provisional. <code>limite_mensual_clp</code> es null para aprobado (sin límite de IntiCheck). Nunca se entregan motivos, coincidencias de listas, PEP, fotos ni documentos. Cada consulta queda en la bitácora.</p>
    <h3 className="font-semibold">Ver documentos del cliente (sin guardarlos)</h3>
    <Code>{`GET ${BASE}/api/integracion/clientes/CLI-1234/documentos
X-API-Key: <tu clave>
X-Actor: oficial@tuempresa.cl

→ {"inticheck_id": "…", "vence_en_segundos": 300,
   "imagenes": [{"tipo": "doc_frente", "url": "${BASE}/api/integracion/documentos/…", "expira_at": 1767225600, "capturada_at": "…"}]}`}</Code>
    <p><code>X-Actor</code> (correo de quien mira) es obligatorio. Cada url vence en 5 minutos y entrega la imagen con marca de agua (empresa, uso exclusivo de cumplimiento, X-Actor, fecha y hora) y <code>Cache-Control: no-store</code>: muéstrala, no la guardes. Funciona en cualquier estado del cliente. Máximo 30 consultas por minuto por empresa; cada consulta queda en la bitácora. IntiCheck no guarda selfies (la verificación facial la hace Didit), así que normalmente solo vendrán doc_frente y doc_reverso.</p>
    <h3 className="font-semibold">Expediente completo (un solo llamado)</h3>
    <Code>{`GET ${BASE}/api/integracion/clientes/CLI-1234/expediente
X-API-Key: <tu clave>
X-Actor: oficial@tuempresa.cl

→ {"estado", "motivos_revision": [...], "regla_aplicada", "ultima_verificacion": {"fecha", "procesamiento", "didit_estado"},
   "documento": {"tipo", "numero", "run", "pais_emisor", "nacionalidad", "nombres", "apellidos", "fecha_nacimiento", "emision", "vencimiento"},
   "declarados": {"domicilio", "telefono", "telefono_verificado", "correo", "ocupacion", "proposito", "rango_montos", "origen_fondos", "pep"},
   "pasos": {"documento", "prueba_vida": {"puntaje"}, "rostro": {"puntaje"}, "telefono", "ip": {"ip", "pais", "vpn_o_tor", "dispositivo"}, "listas"},
   "listas": [{"lista", "nombre_coincidente", "similitud_pct", "pais", "fecha_nacimiento"}], "avisos_didit": [...],
   "busquedas_listas": [{"fuente": "Lista SDN de OFAC…" | "AML de Didit…", "resultado": "sin_coincidencia", "fecha", "coincidencias": 0}],
   "documentos": [{"tipo": "doc_frente", "url": "…", "expira_at"}], "historial": [{"fecha", "accion", "responsable", "motivo"}]}`}</Code>
    <p>Disponible en cualquier estado y queda en la bitácora. Frente y reverso salen de la copia cifrada que guarda IntiCheck (5 años); la selfie solo mientras la sesión de Didit exista. Te enviamos el webhook <code>cliente.verificacion_completada</code> cada vez que termina una verificación nueva, además de <code>cliente.estado_actualizado</code> cuando cambia el estado.</p>
    <h3 className="font-semibold">Revisar un cliente desde tu plataforma</h3>
    <Code>{`GET ${BASE}/api/integracion/clientes/CLI-1234/revision
X-API-Key: <tu clave>

→ {"inticheck_id": "…", "estado": "en_revision", "limite_mensual_clp": null,
   "motivos": ["coincidencia en listas ONU/OFAC/sanciones", "WhatsApp no confirmado"],
   "didit": {"estado": "Approved", "documento": {"estado": "Approved", "avisos": []},
             "prueba_vida": {"estado": "Approved", "puntaje": 92, "avisos": []}, "rostro": {"estado": "Approved", "puntaje": 88, "avisos": []},
             "telefono": {"estado": "Approved", "verificado": true, "canal": "whatsapp", "avisos": []},
             "ip": {"estado": "Approved", "pais": "CL", "vpn_o_tor": false}, "listas": {"estado": "Approved", "puntaje": 0, "coincidencias": 0}},
   "listas": [{"fuente": "IntiCheck", "lista": "Lista SDN de OFAC (Tesoro de EE.UU.)", "caso_estado": "abierto",
               "nombre_coincidente": "…", "similitud_pct": 91.5, "fecha_nacimiento": "1975", "pais": "…"}],
   "cliente": { …mismos datos que el GET de aprobados… }}`}</Code>
    <p>Disponible en cualquier estado (es para que tu oficial revise). Cada consulta queda en la bitácora. <code>rostro.puntaje</code> es el % de parecido que informa Didit.</p>
    <h3 className="font-semibold">Decidir desde tu plataforma</h3>
    <Code>{`POST ${BASE}/api/integracion/clientes/CLI-1234/decision
X-API-Key: <tu clave>
X-Actor: oficial@tuempresa.cl
Content-Type: application/json

{"decision": "aprobar", "motivo": "Identidad revisada por el oficial, documentos en regla",
 "coincidencias_descartadas": true,
 "explicacion": "Homónimo: fecha de nacimiento y país distintos al registro OFAC"}

→ {"ok": true, "estado": "aprobado", "inticheck_id": "…", "estado_cliente": "aprobado", "limite_mensual_clp": null}`}</Code>
    <p>Decisiones: <code>aprobar</code>, <code>aprobar_provisional</code> (acepta <code>limite_mensual_clp</code>; si no lo envías se usa el de tu configuración), <code>requiere_actualizacion</code> y <code>rechazar</code>. <code>motivo</code> es obligatorio (mínimo 10 caracteres; si falta → 400) y <code>X-Actor</code> también (→ 400).</p>
    <p>Se aplica igual que en el panel de IntiCheck: mismas validaciones, mismo webhook de cambio de estado y misma bitácora, con responsable «X-Actor (vía API)». Si hay coincidencias en listas sin resolver, <code>aprobar</code> exige <code>coincidencias_descartadas: true</code> y <code>explicacion</code> (o <code>coincidencias_explicacion</code>, mínimo 20 caracteres) del homónimo (si no → 409); esos casos quedan cerrados como descarte con esa explicación. En la misma llamada, si la identidad estaba en revisión, queda confirmada por X-Actor. Nunca se puede aprobar si Didit rechazó el documento por posible falsificación (→ 409 con el motivo). Si el cliente es PEP o de riesgo alto, «aprobar» queda pendiente de la aprobación del owner en el panel.</p>
    <h3 className="font-semibold">3. Webhook de cambio de estado</h3>
    <Code>{`POST <tu URL de webhook>
X-IntiCheck-Signature: sha256=<hex>
X-IntiCheck-Event-Id: <event_id>

{"event_id": "…", "tipo": "cliente.estado_actualizado", "external_ref": "CLI-1234", "inticheck_id": "…",
 "estado": "aprobado", "limite_mensual_clp": null, "actualizado_at": "…"}`}</Code>
    <p>Sin datos personales: cuando lo necesites, consulta el GET. Responde 2xx; si no, reintentamos a los 1, 5 y 30 minutos. Usa <code>event_id</code> para ignorar repetidos. El webhook de prueba llega con <code>tipo: "webhook.prueba"</code>.</p>
    <h3 className="font-semibold">Verificar la firma (Node.js)</h3>
    <Code>{`const crypto = require("crypto");
// raw = cuerpo crudo (Buffer) tal como llegó, antes de parsear el JSON
const esperado = "sha256=" + crypto.createHmac("sha256", process.env.INTICHECK_WEBHOOK_SECRET).update(raw).digest("hex");
const ok = crypto.timingSafeEqual(Buffer.from(esperado), Buffer.from(req.headers["x-inticheck-signature"] || ""));`}</Code>
    <h3 className="font-semibold">Verificar la firma (Python)</h3>
    <Code>{`import hmac, hashlib
esperado = "sha256=" + hmac.new(SECRETO.encode(), raw_body, hashlib.sha256).hexdigest()
ok = hmac.compare_digest(esperado, request.headers.get("X-IntiCheck-Signature", ""))`}</Code>
    <h3 className="font-semibold">Enlace a la ficha</h3>
    <p><code>{`${BASE}/panel/clientes/{inticheck_id}`}</code> abre la ficha; solo la ven los usuarios de tu empresa.</p>
  </section>
);

export default function IntegracionApi() {
  const [tick, setTick] = useState(0);
  return (
    <div className="space-y-8" data-testid="integracion-api-page">
      <PageHeader overline="Empresa" title="Integración API" />
      <IntegracionCard onChange={() => setTick((t) => t + 1)} />
      <Envios tick={tick} />
      <Docs />
    </div>
  );
}
